/// <reference types="vite/client" />

const API_URL = import.meta.env.VITE_API_URL || 'https://api.prima.myx.is';

function cleanParams(params?: Record<string, unknown>): Record<string, string> {
  if (!params) return {};
  return Object.fromEntries(
    Object.entries(params)
      .filter(([, v]) => v !== undefined && v !== null && v !== '')
      .map(([k, v]) => [k, String(v)])
  );
}

interface ApiOptions extends RequestInit {
  token?: string;
}

class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(
  endpoint: string,
  options: ApiOptions = {}
): Promise<T> {
  const { token, ...fetchOptions } = options;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(fetchOptions.headers as Record<string, string>),
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_URL}${endpoint}`, {
    ...fetchOptions,
    headers,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({
      error: { code: 'UNKNOWN', message: 'Villa kom upp' },
    }));
    throw new ApiError(
      response.status,
      error.error?.code || 'UNKNOWN',
      error.error?.message || 'Villa kom upp'
    );
  }

  return response.json();
}

// Auth
export const auth = {
  getLoginUrl: () => `${API_URL}/api/auth/google`,

  // Development login (bypasses OAuth - remove in production)
  devLogin: () =>
    request<{ success: boolean; token: string; refreshToken: string; user: any }>('/api/dev/login', {
      method: 'POST',
    }),

  refresh: (refreshToken: string) =>
    request<{ token: string }>('/api/auth/refresh', {
      method: 'POST',
      body: JSON.stringify({ refresh_token: refreshToken }),
    }),

  logout: (refreshToken: string) =>
    request('/api/auth/logout', {
      method: 'POST',
      body: JSON.stringify({ refresh_token: refreshToken }),
    }),
};

// Leads
export interface Lead {
  id: string;
  agency_id: string;
  property_id?: string;
  assigned_agent_id?: string;
  caller_name?: string;
  caller_phone: string;
  caller_email?: string;
  inquiry_type?: string;
  lead_score: number;
  status: string;
  source: string;
  notes?: string;
  property_address?: string;
  agent_name?: string;
  created_at: string;
  updated_at: string;
}

export interface LeadStats {
  total: number;
  new_count: number;
  booked: number;
  avg_score: number;
}

export const leads = {
  list: (token: string, params?: { limit?: number; offset?: number; status?: string }) =>
    request<{ data: Lead[]; pagination: { total: number; limit: number; offset: number } }>(
      `/api/leads?${new URLSearchParams(cleanParams(params))}`,
      { token }
    ),
  
  get: (token: string, id: string) =>
    request<Lead>(`/api/leads/${id}`, { token }),
  
  create: (token: string, data: Partial<Lead>) =>
    request<Lead>('/api/leads', {
      method: 'POST',
      token,
      body: JSON.stringify(data),
    }),
  
  update: (token: string, id: string, data: Partial<Lead>) =>
    request<Lead>(`/api/leads/${id}`, {
      method: 'PATCH',
      token,
      body: JSON.stringify(data),
    }),
  
  delete: (token: string, id: string) =>
    request(`/api/leads/${id}`, { method: 'DELETE', token }),
  
  stats: (token: string) =>
    request<LeadStats>('/api/leads/stats/summary', { token }),
};

// Calls
export interface Call {
  id: string;
  agency_id: string;
  lead_id?: string;
  twilio_call_sid: string;
  caller_phone: string;
  called_number: string;
  duration_seconds: number;
  outcome: string;
  transcript?: string;
  recording_url?: string;
  ai_handled: boolean;
  transferred_to?: string;
  created_at: string;
}

export const calls = {
  list: (token: string, params?: { limit?: number; offset?: number; outcome?: string; date_from?: string; date_to?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.offset) searchParams.set('offset', String(params.offset));
    if (params?.outcome) searchParams.set('outcome', params.outcome);
    if (params?.date_from) searchParams.set('date_from', params.date_from);
    if (params?.date_to) searchParams.set('date_to', params.date_to);
    return request<{ data: Call[]; pagination: { total: number } }>(
      `/api/calls?${searchParams.toString()}`,
      { token }
    );
  },

  get: (token: string, id: string) =>
    request<Call>(`/api/calls/${id}`, { token }),

  update: (token: string, id: string, data: Partial<Pick<Call, 'lead_id'>>) =>
    request<Call>(`/api/calls/${id}`, {
      method: 'PATCH',
      token,
      body: JSON.stringify(data),
    }),

  getRecordingUrl: (token: string, id: string) =>
    `${API_URL}/api/calls/${id}/recording?token=${token}`,

  getTranscript: (token: string, id: string) =>
    request<{ transcript: string }>(`/api/calls/${id}/transcript`, { token }),

  stats: (token: string) =>
    request<{ total: number; today: number }>('/api/calls/stats/summary', { token }),

  daily: (token: string, days = 7) =>
    request<{ date: string; count: number }[]>(`/api/calls/stats/daily?days=${days}`, { token }),

  today: (token: string, limit = 10) => {
    const today = new Date();
    const dateStr = today.toISOString().split('T')[0];

    return request<{ data: Call[]; pagination: { total: number } }>(
      `/api/calls?date_from=${dateStr}&date_to=${dateStr}&limit=${limit}`,
      { token }
    );
  },
};

// Properties
export interface Property {
  id: string;
  agency_id: string;
  agent_id?: string;
  address: string;
  postal_code?: string;
  city?: string;
  property_type: string;
  size_sqm?: number;
  rooms?: number;
  bedrooms?: number;
  bathrooms?: number;
  price?: number;
  description?: string;
  status: string;
  images?: string[];
  image_captions?: Record<string, string>;
  image_scores?: Record<string, { category: string; category_en?: string; score: number; quality: number; total: number; confidence: number; suggested_order?: number; detected_objects?: string[] }>;
  main_image_url?: string;
  agent_name?: string;
  agent_phone?: string;
  created_at: string;
  updated_at: string;
}

export const properties = {
  list: (token: string, params?: { limit?: number; offset?: number; status?: string }) =>
    request<{ data: Property[]; pagination: { total: number } }>(
      `/api/properties?${new URLSearchParams(cleanParams(params))}`,
      { token }
    ),

  get: (token: string, id: string) =>
    request<Property>(`/api/properties/${id}`, { token }),

  create: (token: string, data: Partial<Property>) =>
    request<Property>('/api/properties', {
      method: 'POST',
      token,
      body: JSON.stringify(data),
    }),

  update: (token: string, id: string, data: Partial<Property>) =>
    request<Property>(`/api/properties/${id}`, {
      method: 'PATCH',
      token,
      body: JSON.stringify(data),
    }),

  delete: (token: string, id: string) =>
    request(`/api/properties/${id}`, { method: 'DELETE', token }),

  uploadImage: async (token: string, propertyId: string, file: File): Promise<{ success: boolean; key: string; url: string }> => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await fetch(`${API_URL}/api/properties/${propertyId}/images`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({
        error: { code: 'UNKNOWN', message: 'Villa kom upp' },
      }));
      throw new ApiError(
        response.status,
        error.error?.code || 'UNKNOWN',
        error.error?.message || 'Villa kom upp við að hlaða upp mynd'
      );
    }

    return response.json();
  },

  getImageUrl: (propertyId: string, key: string) => {
    // Extract just the filename from the R2 key (e.g. "properties/id/file.jpg" → "file.jpg")
    const filename = key.includes('/') ? key.split('/').pop()! : key;
    return `${API_URL}/api/properties/${propertyId}/images/${encodeURIComponent(filename)}`;
  },

  deleteImage: (token: string, propertyId: string, key: string) => {
    // Extract just the filename from the R2 key (e.g. "properties/id/file.jpg" → "file.jpg")
    const filename = key.includes('/') ? key.split('/').pop()! : key;
    return request(`/api/properties/${propertyId}/images/${encodeURIComponent(filename)}`, {
      method: 'DELETE',
      token,
    });
  },
};

// Viewings
export interface Viewing {
  id: string;
  agency_id: string;
  property_id: string;
  lead_id: string;
  agent_id: string;
  scheduled_at: string;
  duration_minutes: number;
  status: string;
  notes?: string;
  property_address?: string;
  lead_name?: string;
  agent_name?: string;
  created_at: string;
}

export const viewings = {
  list: (token: string, params?: { from?: string; to?: string; status?: string }) =>
    request<{ data: Viewing[]; pagination: { total: number } }>(
      `/api/viewings?${new URLSearchParams(params as Record<string, string>)}`,
      { token }
    ),
  
  get: (token: string, id: string) =>
    request<Viewing>(`/api/viewings/${id}`, { token }),
  
  create: (token: string, data: Partial<Viewing>) =>
    request<Viewing>('/api/viewings', {
      method: 'POST',
      token,
      body: JSON.stringify(data),
    }),
  
  update: (token: string, id: string, data: Partial<Viewing>) =>
    request<Viewing>(`/api/viewings/${id}`, {
      method: 'PATCH',
      token,
      body: JSON.stringify(data),
    }),
  
  delete: (token: string, id: string) =>
    request(`/api/viewings/${id}`, { method: 'DELETE', token }),
  
  today: (token: string) =>
    request<Viewing[]>('/api/viewings/today/list', { token }),
};

// Agents
export interface Agent {
  id: string;
  agency_id: string;
  name: string;
  phone?: string;
  email: string;
  specialization?: string;
  active: boolean;
  accepts_leads: boolean;
  max_leads_per_day?: number;
  total_leads?: number;
  closed_deals?: number;
  created_at: string;
}

export const agents = {
  list: (token: string, includeInactive = false) =>
    request<{ data: Agent[] }>(`/api/agents${includeInactive ? '?include_inactive=true' : ''}`, { token }),
  
  get: (token: string, id: string) =>
    request<Agent>(`/api/agents/${id}`, { token }),
  
  create: (token: string, data: Partial<Agent>) =>
    request<Agent>('/api/agents', {
      method: 'POST',
      token,
      body: JSON.stringify(data),
    }),
  
  update: (token: string, id: string, data: Partial<Agent>) =>
    request<Agent>(`/api/agents/${id}`, {
      method: 'PATCH',
      token,
      body: JSON.stringify(data),
    }),
  
  delete: (token: string, id: string) =>
    request(`/api/agents/${id}`, { method: 'DELETE', token }),
  
  availability: (token: string, id: string, from: string, to: string) =>
    request<{ busy_times: { scheduled_at: string; duration_minutes: number }[] }>(
      `/api/agents/${id}/availability?from=${from}&to=${to}`,
      { token }
    ),
};

// Dashboard
export interface DashboardStats {
  leads: { total: number; today: number };
  calls: { total: number; today: number };
  viewings: { booked: number };
  properties: { active: number };
}

export const dashboard = {
  stats: (token: string) =>
    request<DashboardStats>('/api/stats', { token }),
};

// Settings
export interface BusinessHours {
  monday?: { open: string; close: string };
  tuesday?: { open: string; close: string };
  wednesday?: { open: string; close: string };
  thursday?: { open: string; close: string };
  friday?: { open: string; close: string };
  saturday?: { open: string; close: string };
  sunday?: { open: string; close: string };
}

export interface NotificationPreferences {
  email_notifications: boolean;
  sms_notifications: boolean;
  notify_new_leads: boolean;
  notify_viewing_reminders: boolean;
  notify_call_summaries: boolean;
}

export interface Agency {
  id: string;
  name: string;
  phone?: string;
  email?: string;
  website?: string;
  address?: string;
  voice_greeting?: string;
  voice_enabled: boolean;
  business_hours?: BusinessHours;
  timezone?: string;
  notification_preferences?: NotificationPreferences;
  created_at: string;
}

export const settings = {
  get: (token: string) =>
    request<Agency>('/api/settings', { token }),

  update: (token: string, data: Partial<Agency>) =>
    request<Agency>('/api/settings', {
      method: 'PATCH',
      token,
      body: JSON.stringify(data),
    }),
};

// Holidays
export interface Holiday {
  id: string;
  agency_id: string;
  date: string;
  name: string;
  type: 'closed' | 'half_day' | 'observance';
  close_at?: string;
  is_national: boolean;
  created_at: string;
}

export const holidays = {
  list: (token: string, params?: { year?: number }) =>
    request<{ data: Holiday[] }>(
      `/api/settings/holidays${params?.year ? `?year=${params.year}` : ''}`,
      { token }
    ),

  create: (token: string, data: { date: string; name: string; type?: string; close_at?: string; is_national?: boolean }) =>
    request<Holiday>('/api/settings/holidays', {
      method: 'POST',
      token,
      body: JSON.stringify(data),
    }),

  seed: (token: string, year: number) =>
    request<{ success: boolean; count: number }>('/api/settings/holidays/seed', {
      method: 'POST',
      token,
      body: JSON.stringify({ year }),
    }),

  delete: (token: string, id: string) =>
    request('/api/settings/holidays/' + id, { method: 'DELETE', token }),
};

// API Keys (admin only - read-only display)
export interface ApiKeys {
  twilio_account_sid?: string;
  twilio_phone_number?: string;
  google_project_id?: string;
  dialogflow_agent_id?: string;
  dialogflow_location?: string;
  agency_api_key?: string;
}

export const apiKeys = {
  get: (token: string) =>
    request<ApiKeys>('/api/settings/api-keys', { token }),
};

// Users
export interface User {
  id: string;
  email: string;
  name: string;
  picture?: string;
  role: 'admin' | 'manager' | 'agent';
  active: boolean;
  last_login_at?: string;
  created_at: string;
}

export const users = {
  list: (token: string) =>
    request<{ data: User[] }>('/api/users', { token }),

  get: (token: string, id: string) =>
    request<User>(`/api/users/${id}`, { token }),

  create: (token: string, data: { email: string; name: string; role?: string }) =>
    request<User>('/api/users', {
      method: 'POST',
      token,
      body: JSON.stringify(data),
    }),

  update: (token: string, id: string, data: Partial<{ name: string; role: string; active: boolean }>) =>
    request<User>(`/api/users/${id}`, {
      method: 'PATCH',
      token,
      body: JSON.stringify(data),
    }),

  delete: (token: string, id: string) =>
    request<{ success: boolean }>(`/api/users/${id}`, { method: 'DELETE', token }),
};

// Calendar OAuth
export interface CalendarStatus {
  connected: boolean;
  email?: string;
  connectedAt?: number;
  tokenExpired?: boolean;
}

export const calendar = {
  // Get OAuth authorization URL
  getAuthUrl: (token: string) =>
    request<{ authUrl: string }>('/api/auth/google/calendar', { token }),

  // Get connection status
  status: (token: string) =>
    request<CalendarStatus>('/api/auth/google/calendar/status', { token }),

  // Disconnect calendar
  disconnect: (token: string) =>
    request<{ success: boolean }>('/api/auth/google/calendar/disconnect', {
      method: 'POST',
      token,
    }),

  // Refresh token (usually automatic, but can be triggered manually)
  refresh: (token: string) =>
    request<{ success: boolean; expiresAt: number }>('/api/auth/google/calendar/refresh', {
      method: 'POST',
      token,
    }),
};

// AI Tools
export interface AIIconResult {
  success: boolean;
  data: { icon_key: string; icon_data: string };
}

export interface AICleanPhotoResult {
  success: boolean;
  data: {
    photo_key: string;
    photo_data: string;
    detected_objects: string[];
    removed_objects: string[];
  };
}

export interface AIDescriptionResult {
  success: boolean;
  data: {
    title: string;
    description: string;
    highlights: string[];
    room_count_estimate: number;
    condition: string;
    proofread?: {
      corrections: Array<{ original: string; suggestion: string }>;
      correctedText: string;
      hasErrors: boolean;
    };
  };
}

export interface AIPhotoScoreResult {
  success: boolean;
  data: {
    category: string;
    category_en: string;
    score: number;
    quality_bonus: number;
    total_score: number;
    detected_objects: string[];
    suggested_order: number;
    confidence: number;
  };
}

export interface AIScoreAllResult {
  success: boolean;
  data: {
    scores: Record<string, {
      category: string;
      category_en: string;
      score: number;
      quality: number;
      total: number;
      confidence: number;
      suggested_order: number;
      detected_objects: string[];
    }>;
    scored: number;
    failed: number;
    total: number;
    suggested_hero: string | null;
  };
}

export const ai = {
  generateIcon: (token: string, image: string, propertyType?: string, propertyId?: string) =>
    request<AIIconResult>('/api/ai/property-icon', {
      method: 'POST',
      token,
      body: JSON.stringify({ image, property_type: propertyType, property_id: propertyId }),
    }),

  cleanPhoto: (token: string, image: string) =>
    request<AICleanPhotoResult>('/api/ai/clean-photo', {
      method: 'POST',
      token,
      body: JSON.stringify({ image }),
    }),

  describeProperty: (token: string, image: string, propertyData?: { size_sqm?: number; location?: string; property_type?: string; price?: number }) =>
    request<AIDescriptionResult>('/api/ai/describe-property', {
      method: 'POST',
      token,
      body: JSON.stringify({ image, ...propertyData }),
    }),

  generateCelebration: (token: string, agentName: string, address: string, propertyType: string) =>
    request<{ success: boolean; data: { image_data: string } }>('/api/ai/celebration', {
      method: 'POST',
      token,
      body: JSON.stringify({ agent_name: agentName, address, property_type: propertyType }),
    }),

  scorePhoto: (token: string, image: string, propertyId?: string, imageKey?: string) =>
    request<AIPhotoScoreResult>('/api/ai/score-photo', {
      method: 'POST',
      token,
      body: JSON.stringify({ image, property_id: propertyId, image_key: imageKey }),
    }),

  scoreAllPhotos: (token: string, propertyId: string) =>
    request<AIScoreAllResult>('/api/ai/score-all-photos', {
      method: 'POST',
      token,
      body: JSON.stringify({ property_id: propertyId }),
    }),
};

export { ApiError };
